// SCR-234: the fleet quota overview (API-238), AllCode staff only (`billing:read`, which a
// super-admin holds). Every org with an active quota, fullest first: its fullest standing as a
// bar, who pays its AWS bill, how many quotas and guardrail quotas it carries and how many of its
// scope instances are in warning, grace or exhausted. Filters by the fullest standing's state and
// by owner; CSV of the same rows. A row opens the org's quotas (SCR-230). There is no console
// route: a customer never sees other orgs.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { moneyPolicyFor } from "@nexus/ui";
import { RouterContext } from "../../router/router";
import type { ScreenProps } from "../../router/route";
import { Field } from "@nexus/ui";
import { Card, Chip, FilterBar, PageHeader } from "@nexus/ui";
import { Empty } from "@nexus/ui";
import { DataTable, type Column } from "@nexus/ui";
import {
  FLEET_COMPARE,
  FLEET_OWNERS,
  FLEET_STATES,
  FLEET_TITLE,
  fleetCsvHref,
  fleetRows,
  loadFleet,
  NO_FLEET_FILTERS,
  ownerFilterLabel,
  type FleetFilters,
  type FleetQuotaStanding,
  type FleetRow,
  type FLEET_PATH,
} from "./fleet-model";
import { loadable, NOT_AVAILABLE, stateLabel, utcMoment, type Loadable } from "./model";
import { UsedBar } from "@nexus/ui/screens/quotas";

const STAFF_MONEY = moneyPolicyFor("staff", null);

const COLUMNS: readonly Column<FleetRow>[] = [
  {
    key: "org",
    header: "Organization",
    cell: (row) => (
      <span class="stack-cell">
        <span>{row.orgName}</span>
        <span class="cell-sub">{row.payer}</span>
      </span>
    ),
    compare: FLEET_COMPARE.org,
  },
  {
    key: "fullest",
    header: "Fullest quota",
    help: "quotas.fleetFullest",
    cell: (row) => (
      <span class="quota-used">
        <UsedBar fraction={row.fraction} tone={row.stateTone} label={`${row.percent} used`} />
        <span>
          {row.percent} <Chip tone={row.stateTone}>{row.stateLabel}</Chip>
          <span class="cell-sub">
            {row.fullestName} · {row.fullestScope}
            {row.amounts && ` · ${row.amounts}`}
          </span>
        </span>
      </span>
    ),
    compare: FLEET_COMPARE.fullest,
  },
  { key: "quotas", header: "Quotas", numeric: true, cell: (row) => row.quotas, compare: FLEET_COMPARE.quotas },
  { key: "guardrailQuotas", header: "Guardrails", help: "quotas.owner", numeric: true, cell: (row) => row.guardrailQuotas, compare: FLEET_COMPARE.guardrailQuotas },
  { key: "warning", header: "Warning", help: "quotas.state", numeric: true, cell: (row) => row.warning, compare: FLEET_COMPARE.warning },
  { key: "grace", header: "In grace", numeric: true, cell: (row) => row.grace, compare: FLEET_COMPARE.grace },
  { key: "exhausted", header: "Exhausted", numeric: true, cell: (row) => row.exhausted, compare: FLEET_COMPARE.exhausted },
];

/** `/quotas` in the AllCode admin. */
export function FleetPage(_props: ScreenProps<typeof FLEET_PATH>) {
  const client = useApi();
  const router = useContext(RouterContext);
  const [filters, setFilters] = useState<FleetFilters>(NO_FLEET_FILTERS);
  const [state, setState] = useState<Loadable<FleetQuotaStanding>>({ kind: "loading" });
  const load = () => {
    setState({ kind: "loading" });
    void loadable(() => loadFleet(client, filters)).then(setState);
  };
  useEffect(load, [client, filters.state, filters.owner]);

  const filtered = filters.state !== "" || filters.owner !== "";
  return (
    <div class="page">
      <PageHeader title={FLEET_TITLE} lede="Every organization with a quota, the one nearest its limit first. Open one to see and change its quotas." />
      <FilterBar>
        <Field id="fleet-state" label="Fullest quota's state" help="quotas.state" compact>
          <select id="fleet-state" value={filters.state} onChange={(event) => setFilters({ ...filters, state: event.currentTarget.value as FleetFilters["state"] })}>
            <option value="">Any state</option>
            {FLEET_STATES.map((value) => (
              <option key={value} value={value}>
                {stateLabel(value)}
              </option>
            ))}
          </select>
        </Field>
        <Field id="fleet-owner" label="Owner" help="quotas.owner" compact>
          <select id="fleet-owner" value={filters.owner} onChange={(event) => setFilters({ ...filters, owner: event.currentTarget.value as FleetFilters["owner"] })}>
            <option value="">Any owner</option>
            {FLEET_OWNERS.map((value) => (
              <option key={value} value={value}>
                {ownerFilterLabel(value)}
              </option>
            ))}
          </select>
        </Field>
      </FilterBar>
      <Card title="Organizations" actions={state.kind === "loaded" && <Chip tone="grey">As of {utcMoment(state.value.asOf)}</Chip>}>
        {state.kind === "unavailable" ? (
          <Empty message={NOT_AVAILABLE} />
        ) : (
          <DataTable
            caption="Organizations by their fullest quota"
            hideCaption
            columns={COLUMNS}
            rows={state.kind === "loaded" ? fleetRows(state.value, STAFF_MONEY) : []}
            rowKey={(row) => row.orgId}
            loading={state.kind === "loading"}
            error={state.kind === "error" ? state.error : null}
            onRetry={load}
            emptyMessage={
              filtered
                ? "No organization matches these filters."
                : "No organization has a quota yet. Guardrail quotas let AllCode cap the AWS spend of accounts it pays for; add one from a customer's Quotas page."
            }
            onRowClick={(row) => router?.navigate(row.href)}
            rowLabel={(row) => `Open the quotas of ${row.orgName}`}
            csvHref={state.kind === "loaded" ? fleetCsvHref(client, filters) : undefined}
          />
        )}
      </Card>
    </div>
  );
}
